import * as qsLib from 'qs';

import { handlePagerDutyApiResponse } from 'src/lib/api/handlePagerDutyApiResponse';
import { getEnvironment } from 'src/lib/featureFlags';

import BaseApi from './baseApi';
import jira from './jira';

const API_KEY = '{{settings.pdApiKey}}';
const V3_AUTH_TOKEN = '{{settings.v3AuthToken}}';

const JIRA_SERVER_V3_ADDON_ID = getEnvironment().jiraServerV3AddonId;
const GENERIC_OUTBOUND_INTEGRATION_ID = getEnvironment().genericOutboundIntegrationId;
const JIRA_SERVER_V4_OUTBOUND_INTEGRATION_ID = getEnvironment().jiraServerV4OutboundIntegrationId;

class PagerDuty {
  constructor(jira) {
    this._jira = jira;
    this._accessToken = null;
    this._accountDomain = null;
    this.apiBaseUrl = getEnvironment().apiBaseUrl;
    this.appBaseUrl = getEnvironment().appBaseUrl;
  }

  configure({ accessToken, apiBaseUrl, appBaseUrl, accountDomain }) {
    if (accountDomain !== undefined) this._accountDomain = accountDomain;
    if (accessToken !== undefined) this._accessToken = accessToken;
    if (apiBaseUrl !== undefined) this.apiBaseUrl = apiBaseUrl;
    if (appBaseUrl !== undefined) this.appBaseUrl = appBaseUrl;
  }

  async getAbilities(headers = {}) {
    return this._request({
      url: '/abilities',
      method: 'GET',
      headers,
    });
  }

  async getMe(headers = {}) {
    return this._request({
      url: '/users/me',
      method: 'GET',
      headers,
    }).then(u => u.user);
  }

  async getAccountsAddons(qs = {}) {
    return this._request({
      url: '/accounts_addons',
      method: 'GET',
      qs,
    });
  }

  async deleteAccountsAddon(id) {
    return this._request({
      url: `/accounts_addons/${id}`,
      method: 'DELETE',
    });
  }

  async createJiraServerV3AccountsAddon() {
    return this._request({
      url: `/accounts_addons`,
      method: 'POST',
      body: {
        accounts_addon: {
          addon_id: JIRA_SERVER_V3_ADDON_ID
        }
      }
    });
  }

  async getIncidents() {
    return this._request({
      url: '/incidents/?limit=100&sort_by=urgency:desc,created_at:desc',
      method: 'GET',
    });
  }

  async getIncident(id, qs = {}) {
    return this._request({
      url: `/incidents/${id}`,
      method: 'GET',
      qs,
    }).then(({ incident }) => incident);
  }

  async putIncidentExternalReference(incidentId, webhookId, externalId, externalUrl, summary) {
    return this._request({
      method: 'PUT',
      url: `/incidents/${incidentId}`,
      body: {
        incident: {
          type: 'incident_reference',
          external_references: [{
            webhook: {
              id: webhookId,
              type: 'webhook_reference'
            },
            type: 'incident_external_reference',
            sync: true,
            external_id: externalId,
            external_url: externalUrl,
            label: summary,
            summary
          }]
        }
      },
    });
  }

  async removeIncidentExternalReference(incidentId, externalReferenceId) {
    return this._request({
      method: 'PUT',
      url: `/incidents/${incidentId}`,
      body: {
        incident: {
          type: 'incident_reference',
          external_references: [{
            id: externalReferenceId,
            _destroy: true
          }]
        }
      }
    });
  }

  async getUsers(query, headers = {}) {
    return this._request({
      url: `/users`,
      qs: { query },
      method: 'GET',
      headers,
    });
  }

  async getEscalationPolicies(query, headers = {}) {
    return this._request({
      url: `/escalation_policies`,
      qs: { query },
      method: 'GET',
      headers,
    });
  }

  async getUser(id, headers = {}) {
    return this._request({
      url: `/users/${id}`,
      method: 'GET',
      headers,
    }).then(u => u.user);
  }

  async getTeam(id, headers = {}) {
    return this._request({
      url: `/teams/${id}`,
      method: 'GET',
      headers,
    }).then(t => t.team);
  }

  async getEscalationPolicy(id, headers = {}) {
    return this._request({
      url: `/escalation_policies/${id}`,
      method: 'GET',
      headers,
    }).then(e => e.escalation_policy);
  }

  async getIncidentsByKey(incident_key) {
    return this._request({
      url: `/incidents`,
      qs: { incident_key },
      method: 'GET',
    });
  }

  async acknowledgeIncident(id) {
    return this._request({
      url: `/incidents/${id}`,
      method: 'PUT',
      body: {
        incident: {
          type: 'incident_reference',
          status: 'acknowledged',
        }
      }
    });
  }

  async resolveIncident(id) {
    return this._request({
      url: `/incidents/${id}`,
      method: 'PUT',
      body: {
        incident: {
          type: 'incident_reference',
          status: 'resolved',
        }
      }
    });
  }

  async reassignIncident(id, users, escalationPolicy, escalationLevel) {
    const incident = {
      type: 'incident_reference',
    };

    if (users && users.length) {
      incident.assignments = users.map(user => ({ assignee: {
        id: user.id,
        type: 'user_reference'
      } }));
    }

    if (escalationPolicy) {
      incident.escalation_policy = {
        id: escalationPolicy.id,
        type: 'escalation_policy_reference',
      };
    }

    if (escalationLevel) {
      incident.escalation_level = escalationLevel;
    }

    return this._request({
      url: `/incidents/${id}`,
      method: 'PUT',
      body: { incident }
    });
  }

  async getPagerDutyServices(query) {
    return this._request({
      url: `/services`,
      qs: { query },
      method: 'GET',
    });
  }

  async createIncident(incident, headers = {}) {
    return this._request({
      url: '/incidents',
      method: 'POST',
      body: {
        incident
      },
      headers
    });
  }

  async getIncidentLogEntries(incidentId, incidentAccountDomain) {
    const headers = {};
    if (incidentAccountDomain !== this._accountDomain) {
      headers.Authorization = `Token token=${API_KEY}`;
    }

    return this._request({
      url: `/incidents/${incidentId}/log_entries`,
      method: 'GET',
      headers,
    });
  }

  async addNote(incidentId, note) {
    return this._request({
      url: `/incidents/${incidentId}/notes`,
      method: 'POST',
      body: {
        note: {
          content: `${note}`,
        }
      }
    });
  }

  async getNotes(incidentId, incidentAccountDomain) {
    const headers = {};
    if (incidentAccountDomain !== this._accountDomain) {
      headers.Authorization = `Token token=${API_KEY}`;
    }
    return this._request({
      url: `/incidents/${incidentId}/notes`,
      method: 'GET',
      headers,
    });
  }

  async listIncidentWorkflowsTriggers(qs) {
    return this._request({
      method: 'GET',
      url: `/incident_workflows/triggers`,
      qs,
      headers: {
        'X-EARLY-ACCESS': 'incident-workflows-early-access',
      },
    });
  }

  async getIncidentWorkflow(id) {
    return this._request({
      url: `/incident_workflows/${id}`,
      method: 'GET',
      headers: {
        'X-EARLY-ACCESS': 'incident-workflows-early-access',
      },
    }).then(r => r.incident_workflow);
  }

  async triggerIncidentWorkflow(triggerId, incidentId, { sourceName } = {}) {
    return this._request({
      method: 'POST',
      url: `/incident_workflows/triggers/${triggerId}/start`,
      headers: {
        'X-EARLY-ACCESS': 'incident-workflows-early-access',
      },
      body: {
        incident_id: incidentId,
        source_name: sourceName || 'Jira Integration',
      }
    });
  }

  async getPriorities() {
    try {
      return await this._request({
        url: `/priorities`,
        method: 'GET',
      });
    } catch (e) {
      if (e && typeof e.message === 'string' && e.message.includes('status = 404')) {
        throw new Error('Priorities are not enabled for your account');
      }
      throw e;
    }
  }
  async setPriority(incidentId, priority) {
    return this._request(
      {
        method: 'PUT',
        url: `/incidents/${incidentId}`,
        body: {
          incident: {
            type: 'incident',
            priority
          }
        },
      });
  }

  async getWebhook(id, qs) {
    return this._request({
      url: `/webhooks/${id}`,
      qs,
      method: 'GET',
    }).then(res => res.webhook);
  }

  async getWebhooks(query, qs) {
    return this._request({
      url: `/webhooks`,
      qs: {
        ...qs || {},
        query
      },
      method: 'GET',
    });
  }

  async createJiraWebhook(name, serviceId, config) {
    return this.createWebhook(name, undefined, serviceId, config, await this.resolveJiraOutboundIntegrationId());
  }

  async createGenericWebhook(name, url, serviceId) {
    return this.createWebhook(name, url, serviceId, undefined, await this.resolveGenericOutboundIntegrationId());
  }

  async createWebhook(name, url, serviceId, config, oiId) {
    return this._request({
      url: `/webhooks`,
      method: 'POST',
      body: {
        webhook: {
          name,
          endpoint_url: url,
          config,
          webhook_object: {
            type: 'service_reference',
            id: serviceId,
          },
          outbound_integration: {
            id: oiId,
            type: 'outbound_integration_reference',
          }
        }
      }
    }).then(res => res.webhook);
  }

  async deleteWebhook(id) {
    return this._request({
      url: `/webhooks/${id}`,
      method: 'DELETE',
    });
  }

  async getAllWebhookSubscriptions() {
    const result = [];
    let offset = 0;
    let more = true;
    while (more) {
      const { webhook_subscriptions, more: newMore } = await this._request({
        method: 'GET',
        url: `/apps/webhook_subscriptions`,
        qs: { offset },
      });

      more = newMore;
      result.push(...webhook_subscriptions);
      offset = result.length;
    }

    return result;
  }

  async createWebhookSubscription(webhook_subscription) {
    return this._request({
      method: 'POST',
      url: `/apps/webhook_subscriptions`,
      body: { webhook_subscription }
    });
  }

  async deleteWebhookSubscription(id) {
    return this._request({
      method: 'DELETE',
      url: `/apps/webhook_subscriptions/${id}`,
    });
  }


  async resolveJiraOutboundIntegrationId() {
    return JIRA_SERVER_V4_OUTBOUND_INTEGRATION_ID;
  }

  async resolveGenericOutboundIntegrationId() {
    return GENERIC_OUTBOUND_INTEGRATION_ID;
  }

  async addResponders(requesterId, incidentId, message, users, escalationPolicies) {
    const userTargets = (users || []).map(user => ({
      responder_request_target: {
        id: user.id,
        type: 'user_reference',
      }
    }));

    const escalationPolicyTargets = (escalationPolicies || []).map(escalationPolicy => ({
      responder_request_target: {
        id: escalationPolicy.id,
        type: 'escalation_policy_reference',
      }
    }));

    return this._request({
      url: `/incidents/${incidentId}/responder_requests`,
      method: 'POST',
      body: {
        requester_id: requesterId,
        message,
        responder_request_targets: [
          ...userTargets,
          ...escalationPolicyTargets,
        ],
      }
    });
  }

  async createIncidentViaLegacySidebarApi(serviceId, title, description, issue, currentUserName, headers = {}) {
    const fields = issue.fields || {};

    const extractDisplayName = u => {
      const {
        displayName,
        name,
        emailAddress,
        key
      } = u || {};
      return displayName || name || emailAddress || key || 'Unknown user';
    };

    return this._request({
      method: 'POST',
      url: `${this.appBaseUrl}/jira_server_views/create_incident`,
      body: {
        jwt: V3_AUTH_TOKEN,
        service_id: serviceId,
        summary: title,
        jira_user_name: currentUserName,
        description,
        issue_key: issue.key,
        issue_type: (fields.issuetype || {}).name,
        reporter_name: extractDisplayName(fields.reporter),
        status: (fields.status || {}).name,
        jira_priority_id: (fields.priority || {}).id
      },
      interpolateBody: true,
    });
  }

  _getLegacySidebarInfoCache = {};
  getLegacySidebarInfo(issueKey, issueTypeId) {
    const key = `${issueKey}-${issueTypeId}`;

    this._getLegacySidebarInfoCache[key] = this._getLegacySidebarInfoCache[key] || this._request({
      url: `${this.appBaseUrl}/jira_server_views/sidebar_info?issue_key=${issueKey}&issue_type=${issueTypeId}&jwt=${V3_AUTH_TOKEN}`,
      method: 'GET',
      needProxy: true,
    });

    this._getLegacySidebarInfoCache[key].catch(() => {});
    return this._getLegacySidebarInfoCache[key];
  }

  _request({ method, url, body, headers, qs, interpolateBody = false, needProxy = false }) {
    if (url.indexOf('http') !== 0) {
      url = this.apiBaseUrl + url;
    }

    const query = qsLib.stringify(qs, { arrayFormat: 'brackets' });
    if (query) {
      if (url.includes('?')) url = `${url}&${query}`;
      else url = `${url}?${query}`;
    }

    headers = headers || {};
    if (!headers.Accept) {
      headers.Accept = 'application/vnd.pagerduty+json;version=2';
    }

    if (!headers.Authorization) {
      if (this._accessToken) {
        headers.Authorization = `Bearer ${this._accessToken}`;
      } else {
        headers.Authorization = `Token token=${API_KEY}`;
      }
    }

    if (!headers['X-PagerDuty-Client']) {
      headers['X-PagerDuty-Client'] = `"JiraServiceClient" <${location.origin + location.pathname}>`;
    }

    const needProxyAllConditions = needProxy || interpolateBody || headers.Authorization === `Token token=${API_KEY}`;
    const response = needProxyAllConditions
      ? this._jira.proxyRequest(method, url, body, headers, interpolateBody)
      : BaseApi.regularRequest(method, url, body, headers);

    if (!response) {
      // only possible in tests with bad stubs
      console.error('The request has not been stubbed! Request details: ');
      console.error({
        needProxyAllConditions,
        method,
        url,
        body,
        headers,
        interpolateBody
      });

      throw new Error(`Calling method that has not been stubbed: ${method} ${url}`);
    }

    return response
      .then(({ status, responseText }) => handlePagerDutyApiResponse({ headers, url, status, responseText }));
  }
}

window.__pd_client = window.__cy_stub_pd_client || new PagerDuty(jira);
export default window.__pd_client;
